/**
 * The live map's view of the fleet: one row per bus the map accounts for,
 * joined from the roster, the position stream, device sessions and running
 * trips, with its lifecycle state resolved exactly once.
 *
 * Layout builds the rows and every surface reads them — the markers, the
 * vehicle list, the detail panel and the counters — so a bus cannot be green
 * on the map, "Standing by" in the list and LATE in the counters at the same
 * moment, which is what three separately-derived answers used to produce.
 *
 * Plain TypeScript with no JSX or browser globals, so the rules (who is on the
 * roster, who is signed in, how the counters partition the fleet, how the list
 * sorts) run under node's test runner: see liveFleet.test.ts.
 */
import type {
  DeviceView,
  FleetStats,
  RouteShape,
  TripInstance,
  Vehicle,
  VehicleLiveState,
  VehiclePosition,
} from "../api/types";
import { driverDisplayName, hasFreshFix, isDeviceOnline, resolveVehicleLiveState } from "../../../shared/types.ts";

/**
 * Stable identity for a marker, a list row and the selection.
 *
 * Bound buses are identified by vehicle, but a tablet reporting before it has
 * been assigned one has no vehicle_id — those fall back to the device.
 * Keying on vehicle_id alone collapsed every unbound tablet onto one marker.
 */
export function markerKey(p: VehiclePosition): string {
  return p.vehicle_id ?? (p.device_id ? `device:${p.device_id}` : "unknown");
}

/**
 * What a row stands for:
 *  - "vehicle":  a bus on the live roster — the only kind the counters count;
 *  - "unlisted": a fix naming a vehicle the roster does not list (it still
 *                gets a marker and a row: a bus on the road is never hidden);
 *  - "tablet":   a tablet reporting with no bus bound to it.
 */
export type LiveRowKind = "vehicle" | "unlisted" | "tablet";

export interface LiveVehicleRow {
  /** markerKey(): the vehicle id, or `device:<id>` for an unbound tablet. */
  key: string;
  kind: LiveRowKind;
  vehicleId?: string;
  deviceId?: string;
  /** Latest fix, possibly hours old — hasFreshFix() says whether it is live. */
  position?: VehiclePosition;
  /** Plate, falling back to label or fleet number; "" when there is none. */
  plate: string;
  /**
   * Who is driving, when the system genuinely knows: the running trip's
   * driver, else a current session's. "" otherwise — never a name off a
   * session left open since the tablet was signed out.
   */
  driver: string;
  /** The trip in progress on this bus, if any. */
  trip?: TripInstance;
  state: VehicleLiveState;
  /** The running trip's schedule deviation, positive = late. */
  delaySeconds?: number;
  /** Vehicle.status ("maintenance", "retired", …) of a non-active bus that is
   *  listed only because it is on the road. */
  inactiveStatus?: string;
  /** Epoch ms the bus was last heard from — the newer of its tablet's last
   *  heartbeat and its last fix — or null when it never has been. */
  lastSeenAt: number | null;
  /** A tablet is bound to this bus (it has an open device session). */
  hasTablet: boolean;
  /** The fix or the tablet says it has had no working session for hours. */
  signedOut: boolean;
  /** routeKeyOf(trip): what the route filter matches on. */
  routeKey: string | null;
  /** Everything the search box matches against, lower-cased. */
  searchText: string;
}

export interface LiveFleetInput {
  vehicles: readonly Vehicle[];
  positions: readonly VehiclePosition[];
  devices: readonly DeviceView[];
  /** In-progress trip per vehicle id. */
  activeTrips: ReadonlyMap<string, TripInstance>;
  now: number;
  /**
   * Whether `vehicles` is the roster or merely has not arrived. Without it a
   * fix naming a vehicle cannot be called "not on the roster" — it is a bus,
   * unlabelled until the roster loads. Defaults to true.
   */
  rosterKnown?: boolean;
}

/** More than this behind schedule counts as LATE. */
export const LATE_THRESHOLD_SEC = 300;

/** More than this ahead of schedule counts as EARLY — far tighter than LATE,
 *  as on-time windows usually are: a bus that leaves early strands riders who
 *  arrived on time. The vehicle list's delay colour and the dashboard's
 *  on-time gauge use the same two bounds. */
export const EARLY_THRESHOLD_SEC = 60;

/** Vehicle.status, normalised. A blank status counts as active: a bus is not
 *  dropped from the map for want of a field. */
function vehicleStatus(v: Vehicle): string {
  return (v.status ?? "").trim().toLowerCase() || "active";
}

/** "maintenance" → "Maintenance". */
export function vehicleStatusLabel(status: string): string {
  const s = status.trim().replace(/_/g, " ");
  return s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : "";
}

/**
 * Identity of the route a trip runs, from the names a trip instance carries
 * (the list endpoint has no route id). The short name alone is not enough:
 * both corridors here are "Orange Line" — the main line and the airport spur —
 * so pills keyed on it lumped them into one, labelled with whichever long name
 * happened to come last.
 */
export function routeKeyOf(trip?: TripInstance): string | null {
  if (!trip) return null;
  const short = (trip.route_short_name ?? "").trim();
  const long = (trip.route_long_name ?? "").trim();
  if (!short && !long) return null;
  return `${short}\u001f${long}`;
}

/** Epoch ms, NaN when absent or unparseable. */
function timeMs(iso: string | undefined): number {
  return iso ? new Date(iso).getTime() : NaN;
}

function newestHeartbeat(devices: readonly DeviceView[]): number | null {
  let newest: number | null = null;
  for (const d of devices) {
    const t = d.last_heartbeat_at;
    if (t != null && t > 0 && (newest == null || t > newest)) newest = t;
  }
  return newest;
}

function buildRow(args: {
  key: string;
  kind: LiveRowKind;
  vehicle?: Vehicle;
  vehicleId?: string;
  position?: VehiclePosition;
  /** Tablets whose heartbeat speaks for this row: the ones bound to the bus,
   *  or the unbound tablet itself. */
  tablets: readonly DeviceView[];
  trip?: TripInstance;
  /** Whether a session on these tablets can name a signed-in driver. */
  namesDriver: boolean;
  now: number;
}): LiveVehicleRow {
  const { key, kind, vehicle, position: p, tablets, trip, now } = args;
  const heartbeatAt = newestHeartbeat(tablets);
  const online = isDeviceOnline(heartbeatAt, p, now);
  // Both the fix and /devices carry the server's signed-out verdict (no
  // working session for three hours). They are computed at different moments
  // and can disagree for a few minutes, so either one is enough.
  const signedOut = !!p?.signed_out || (tablets.length > 0 && tablets.every((d) => d.signed_out));

  // device_sessions rows are not closed when a tablet is signed out or simply
  // switched off: on 2026-10-20 every bus in the fleet still had one, with a
  // driver, from days or weeks before, and the map read "Standing by" under a
  // weeks-old name for buses nobody was signed in on. A session only counts
  // while its tablet is reachable and neither source reports it signed out.
  // A running trip is proof of a driver on its own.
  const session = args.namesDriver ? tablets.find((d) => d.driver_id && !d.signed_out) : undefined;
  const sessionCurrent = !!session && online && !signedOut;
  const tabletTrip = tablets.find((d) => d.trip_status === "in_progress");
  const state = resolveVehicleLiveState(
    {
      position: p,
      lastHeartbeatAt: heartbeatAt,
      loggedIn: sessionCurrent,
      tripInProgress: !!trip || !!tabletTrip,
    },
    now,
  );

  // Suppressed when the "driver" is really a per-vehicle account — "ORG 7503"
  // beside plate ORG-7503 reads as a person and says nothing.
  const driver =
    driverDisplayName(trip?.driver_name) ??
    (sessionCurrent ? driverDisplayName(session?.driver_name) : null) ??
    "";

  const plate = (vehicle?.license_plate || vehicle?.vehicle_label || vehicle?.vehicle_id || "").trim();
  const status = vehicle ? vehicleStatus(vehicle) : "active";
  const fixAt = timeMs(p?.time);
  const seen = [heartbeatAt ?? NaN, fixAt].filter(Number.isFinite);

  return {
    key,
    kind,
    vehicleId: args.vehicleId,
    deviceId: kind === "tablet" ? p?.device_id : undefined,
    position: p,
    plate,
    driver,
    trip,
    state,
    delaySeconds: trip ? trip.delay_seconds : tabletTrip?.delay_seconds,
    inactiveStatus: status === "active" ? undefined : status,
    lastSeenAt: seen.length ? Math.max(...seen) : null,
    hasTablet: tablets.length > 0,
    signedOut,
    routeKey: routeKeyOf(trip),
    searchText: [
      plate,
      vehicle?.vehicle_label,
      vehicle?.vehicle_id,
      driver,
      trip?.route_short_name,
      trip?.route_long_name,
      trip?.headsign,
      kind === "tablet" ? p?.device_id : undefined,
    ]
      .filter(Boolean)
      .join("\n")
      .toLowerCase(),
  };
}

/**
 * One row per bus the live map accounts for.
 *
 * The roster is the active fleet: `/vehicles` also returns buses marked
 * inactive, in maintenance or retired, and those stay off the map and out of
 * the counters — unless one is reporting a fresh fix or running a trip right
 * now, because a bus on the road is never hidden, whatever its paperwork says
 * (its row carries the status so the list can say so). Fixes that name no
 * roster vehicle, and tablets bound to no bus, get rows of their own.
 */
export function buildLiveRows({ vehicles, positions, devices, activeTrips, now, rosterKnown = true }: LiveFleetInput): LiveVehicleRow[] {
  // The snapshot already merges both position pipelines into one fix per bus;
  // keep the newest anyway should a payload ever repeat one.
  const fixByKey = new Map<string, VehiclePosition>();
  for (const p of positions) {
    if (!p.vehicle_id && !p.device_id) continue; // nothing to identify it by
    const k = markerKey(p);
    const prev = fixByKey.get(k);
    if (!prev || timeMs(p.time) > timeMs(prev.time)) fixByKey.set(k, p);
  }

  const tabletsByVehicle = new Map<string, DeviceView[]>();
  const deviceById = new Map<string, DeviceView>();
  for (const d of devices) {
    deviceById.set(d.device_id, d);
    if (d.bound && d.vehicle_id) {
      const list = tabletsByVehicle.get(d.vehicle_id);
      if (list) list.push(d);
      else tabletsByVehicle.set(d.vehicle_id, [d]);
    }
  }

  const rows: LiveVehicleRow[] = [];
  const known = new Set<string>();
  for (const v of vehicles) {
    known.add(v.id);
    const p = fixByKey.get(v.id);
    const trip = activeTrips.get(v.id);
    if (vehicleStatus(v) !== "active" && !hasFreshFix(p, now) && !trip) continue;
    rows.push(buildRow({
      key: v.id, kind: "vehicle", vehicle: v, vehicleId: v.id, position: p,
      tablets: tabletsByVehicle.get(v.id) ?? [], trip, namesDriver: true, now,
    }));
  }

  for (const [key, p] of fixByKey) {
    if (p.vehicle_id) {
      if (known.has(p.vehicle_id)) continue;
      rows.push(buildRow({
        key, kind: rosterKnown ? "unlisted" : "vehicle", vehicleId: p.vehicle_id, position: p,
        tablets: tabletsByVehicle.get(p.vehicle_id) ?? [], trip: activeTrips.get(p.vehicle_id),
        namesDriver: true, now,
      }));
    } else {
      const tablet = p.device_id ? deviceById.get(p.device_id) : undefined;
      // A tablet bound to no bus has no driver the map could name.
      rows.push(buildRow({
        key, kind: "tablet", position: p, tablets: tablet ? [tablet] : [], namesDriver: false, now,
      }));
    }
  }
  return rows;
}

function assertNever(x: never): never {
  throw new Error(`unhandled vehicle state: ${String(x)}`);
}

/**
 * Counters for the roster, partitioned so that every bus lands in exactly one
 * bucket: onTime + late + early + lost + noTrip + idle + noGps + offline ===
 * total.
 *
 * LATE used to take every lost_contact too, and lost_contact also covered a
 * bus moving with no trip at all — so on a day with no trip in progress
 * anywhere, the two signed-out buses driving the route were counted LATE. It
 * also took buses running ahead of schedule; those are EARLY now.
 */
export function computeFleetStats(rows: readonly LiveVehicleRow[]): FleetStats {
  let onTime = 0, late = 0, early = 0, lost = 0, noTrip = 0, idle = 0, noGps = 0, offline = 0;
  for (const r of rows) {
    if (r.kind !== "vehicle") continue;
    switch (r.state) {
      case "on_trip": {
        // A running trip always carries a deviation; it is only missing for
        // the moment the trip list lags the tablet's own view of the trip.
        const delay = r.delaySeconds ?? 0;
        if (delay > LATE_THRESHOLD_SEC) late++;
        else if (delay < -EARLY_THRESHOLD_SEC) early++;
        else onTime++;
        break;
      }
      case "lost_contact": lost++; break;
      case "moving_no_trip": noTrip++; break;
      case "unclaimed":
      case "standing_by": idle++; break;
      case "no_gps": noGps++; break;
      case "offline": offline++; break;
      default: assertNever(r.state);
    }
  }
  const total = onTime + late + early + lost + noTrip + idle + noGps + offline;
  return { onDuty: total - offline - noGps, total, onTime, late, early, lost, noTrip, offline, noGps, idle };
}

/**
 * Dispatcher priority for the vehicle list, most urgent first. Offline sorts
 * last: it used to rank above every live state, so the three buses actually on
 * the road were listed under thirty-two dark ones.
 */
export const STATE_SORT_RANK: Record<VehicleLiveState, number> = {
  lost_contact: 0,
  moving_no_trip: 1,
  no_gps: 2,
  on_trip: 3,
  standing_by: 4,
  unclaimed: 5,
  offline: 6,
};

const plateOrder = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

/** By state, then plate (ORG-7503 before ORG-7512); unnamed tablets last. */
export function compareLiveRows(a: LiveVehicleRow, b: LiveVehicleRow): number {
  return (
    STATE_SORT_RANK[a.state] - STATE_SORT_RANK[b.state] ||
    Number(!a.plate) - Number(!b.plate) ||
    plateOrder.compare(a.plate, b.plate) ||
    a.key.localeCompare(b.key)
  );
}

/** The list's order: buses raising a panic alarm first, then compareLiveRows. */
export function listOrder(panicVehicleIds?: ReadonlySet<string>): (a: LiveVehicleRow, b: LiveVehicleRow) => number {
  const panicking = (r: LiveVehicleRow) => Number(!!r.vehicleId && !!panicVehicleIds?.has(r.vehicleId));
  return (a, b) => panicking(b) - panicking(a) || compareLiveRows(a, b);
}

/** How a row is named on screen: its plate, or for a tablet bound to no bus
 *  the tablet's id — "—" said nothing about which tablet it was. */
export function rowLabel(row: LiveVehicleRow): string {
  return row.plate || (row.kind === "tablet" && row.deviceId ? row.deviceId : "");
}

/** The search box: plate, fleet number, driver, route or headsign. Case and
 *  punctuation are ignored, so "org7512" finds ORG-7512. */
export function rowMatchesQuery(row: LiveVehicleRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (row.searchText.includes(q)) return true;
  const compact = q.replace(/[^a-z0-9]/g, "");
  return compact.length > 0 && row.searchText.replace(/[^a-z0-9]/g, "").includes(compact);
}

/**
 * Whether the route filter shows this row. `pinned` keys are always shown — a
 * bus raising a panic alarm is never filtered off the map.
 */
export function rowOnRoute(row: LiveVehicleRow, routeKey: string | null, pinned?: ReadonlySet<string>): boolean {
  return !routeKey || row.routeKey === routeKey || !!pinned?.has(row.key);
}

export interface RouteOption {
  key: string;
  label: string;
  /** "#RRGGBB" from the route's GTFS colour, or null when it has none. */
  color: string | null;
  /** Buses running a trip on it right now. */
  count: number;
}

/** The route filter's pills: every route a bus is running a trip on. */
export function routeOptions(rows: readonly LiveVehicleRow[]): RouteOption[] {
  const byKey = new Map<string, RouteOption>();
  for (const r of rows) {
    if (!r.routeKey || !r.trip) continue;
    const existing = byKey.get(r.routeKey);
    if (existing) { existing.count++; continue; }
    const t = r.trip;
    byKey.set(r.routeKey, {
      key: r.routeKey,
      label: (t.route_long_name || t.route_short_name || "").trim(),
      color: t.route_color ? `#${t.route_color.replace(/^#/, "")}` : null,
      count: 1,
    });
  }
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** "just now", "12m ago", "6h ago", "3d ago". */
export function formatAgo(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Why an offline bus is dark, as far as the client can tell. /devices only
 *  lists open sessions, so "no tablet" means none is bound now. */
export function offlineDetail(row: LiveVehicleRow, now: number): string {
  if (row.lastSeenAt != null) return `last seen ${formatAgo(now - row.lastSeenAt)}`;
  return row.hasTablet ? "never reported" : "no tablet";
}

/** The vehicle list's second line. */
export function rowSubtitle(row: LiveVehicleRow, now: number): string {
  switch (row.state) {
    case "on_trip":
    case "lost_contact": {
      const direction = row.trip?.headsign || (row.trip ? "Active" : "");
      return `${row.driver || "—"}${direction ? ` · ${direction}` : ""}`;
    }
    // A signed-out tablet is the usual reason a moving bus has no trip, and
    // the one dispatch can act on: get the driver to sign in.
    case "moving_no_trip": return row.signedOut ? "Moving, no trip · signed out" : "Moving, no trip";
    case "standing_by": return row.driver ? `Standing by · ${row.driver}` : "Standing by";
    case "unclaimed": return row.signedOut ? "Unclaimed · signed out" : "Unclaimed";
    case "no_gps": return "No GPS · tablet online";
    case "offline": return `Offline · ${offlineDetail(row, now)}`;
    default: return assertNever(row.state);
  }
}

/** One sentence on what the state means for this bus — the detail panel and
 *  the map popup say the same thing. */
export function stateExplanation(row: LiveVehicleRow, now: number): string {
  const fixAt = row.position ? timeMs(row.position.time) : NaN;
  const lastFix = Number.isFinite(fixAt) ? `last fix ${formatAgo(now - fixAt)}` : "no fix";
  switch (row.state) {
    case "lost_contact": return `Trip in progress, but the bus has gone quiet · ${lastFix}`;
    case "moving_no_trip":
      return row.signedOut
        ? "Moving with no trip claimed — the tablet is signed out, so nothing records this run"
        : "Moving with no trip claimed — nothing records this run";
    case "no_gps": return `Tablet online, but no GPS fix · ${lastFix}`;
    case "offline": return `Tablet unreachable · ${offlineDetail(row, now)}`;
    case "unclaimed":
      return row.signedOut ? "Reporting, nobody signed in — signed out for hours" : "Reporting, nobody signed in";
    case "standing_by": return "Driver signed in, no trip claimed";
    case "on_trip": return "On a trip";
    default: return assertNever(row.state);
  }
}

/** [[south, west], [north, east]]. */
export type LatLngBox = [[number, number], [number, number]];

/** How far outside the route network a live fix may lie and still widen the
 *  opening view (≈ 25 km). Further out it is a bad fix — tablets have reported
 *  Beijing and 0,0 — and fitting to it would open the map on half a continent. */
const NEAR_NETWORK_DEG = 0.25;

/**
 * The view the live map opens on: the whole route network, widened to take in
 * any bus reporting live near it. Null until the route shapes have loaded —
 * without them there is nothing trustworthy to frame.
 */
export function initialMapBounds(
  shapes: readonly RouteShape[],
  positions: Iterable<VehiclePosition>,
  now: number,
): LatLngBox | null {
  let s = Infinity, w = Infinity, n = -Infinity, e = -Infinity;
  for (const shape of shapes) {
    for (const [lat, lon] of shape.coordinates) {
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      s = Math.min(s, lat); n = Math.max(n, lat);
      w = Math.min(w, lon); e = Math.max(e, lon);
    }
  }
  if (!Number.isFinite(s)) return null;
  const [s0, w0, n0, e0] = [s, w, n, e];
  for (const p of positions) {
    if (!hasFreshFix(p, now)) continue;
    const lat = p.location?.latitude;
    const lon = p.location?.longitude;
    if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    if (lat < s0 - NEAR_NETWORK_DEG || lat > n0 + NEAR_NETWORK_DEG) continue;
    if (lon < w0 - NEAR_NETWORK_DEG || lon > e0 + NEAR_NETWORK_DEG) continue;
    s = Math.min(s, lat); n = Math.max(n, lat);
    w = Math.min(w, lon); e = Math.max(e, lon);
  }
  return [[s, w], [n, e]];
}
